// services/kursi-play-v2/kpLiveApiClient.ts
// =============================================================================
// Typed API client for Kursi Play V2 — Live Practice Engine
//
// Wraps all V2 edge function calls with type safety, error normalization,
// and consistent fetch options.
//
// Usage:
//   import { kpLiveApi } from '@/services/kursi-play-v2/kpLiveApiClient';
//   const result = await kpLiveApi.generateWorld({ ... });
// =============================================================================

import type {
  KpGenerateLiveWorldInput,
  KpGenerateLiveWorldResponse,
  KpGenerationStatusResponse,
  KpV2WorldHomeData,
  KpLessonRuntimeData,
  KpSubmitScenarioActionInput,
  KpSubmitScenarioActionResponse,
  KpCompleteLessonV2Response,
} from '@/types/kursi-play-v2';

// ── Config ────────────────────────────────────────────────────────────────────

const SUPABASE_URL     = process.env.EXPO_PUBLIC_SUPABASE_URL     ?? '';
const SUPABASE_ANON_KEY = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ?? '';

function fnUrl(name: string): string {
  return `${SUPABASE_URL}/functions/v1/${name}`;
}

function defaultHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    'apikey': SUPABASE_ANON_KEY,
  };
}

// ── Base fetch ────────────────────────────────────────────────────────────────

async function kpPost<T>(functionName: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(fnUrl(functionName), {
    method:  'POST',
    headers: defaultHeaders(),
    body:    JSON.stringify(body),
  });

  const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));

  if (!res.ok) {
    throw new KpApiError(
      data?.error ?? `Request failed with status ${res.status}`,
      data?.error_code,
      res.status,
    );
  }

  return data as T;
}

// ── Error type ────────────────────────────────────────────────────────────────

export class KpApiError extends Error {
  constructor(
    message: string,
    public readonly errorCode?: string,
    public readonly httpStatus?: number,
  ) {
    super(message);
    this.name = 'KpApiError';
  }
}

// ── API client ────────────────────────────────────────────────────────────────

export const kpLiveApi = {
  /**
   * Generate a new V2 live-practice world from raw content.
   * Long-running (30-90s). Call from a non-blocking context.
   */
  async generateWorld(input: KpGenerateLiveWorldInput): Promise<KpGenerateLiveWorldResponse> {
    return kpPost<KpGenerateLiveWorldResponse>('kp-generate-live-world', {
      raw_content:      input.raw_content,
      world_name:       input.world_name,
      learning_goal:    input.learning_goal,
      language:         input.language,
      user_id:          input.user_id,
      // TASK 5: adaptive generation params
      level:            input.level            ?? 'intermediate',
      time_per_session: input.time_per_session ?? '10',
    });
  },

  /**
   * Poll generation job status (TASK 3).
   * Call every 2s after generateWorld returns { running: true, job_id }.
   */
  async getGenerationStatus(jobId: string): Promise<KpGenerationStatusResponse> {
    return kpPost<KpGenerationStatusResponse>('kp-get-generation-status', { job_id: jobId });
  },

  /**
   * Get world home data.
   * Returns schema_version in response; caller should check it.
   * V2 response: { schema_version:"live_practice_v1", world, lessons[], progress }
   */
  async getWorldHome(slug: string, userId: string): Promise<KpV2WorldHomeData> {
    return kpPost<KpV2WorldHomeData>('kp-get-world-home', { slug, user_id: userId });
  },

  /**
   * Get runtime lesson data (scenarios + actions, no consequences yet).
   * Only works for live_practice_v1 worlds.
   */
  async getLessonRuntime(lessonId: string, slug: string, userId: string): Promise<KpLessonRuntimeData> {
    return kpPost<KpLessonRuntimeData>('kp-get-lesson-runtime', {
      lesson_id: lessonId,
      slug,
      user_id:   userId,
    });
  },

  /**
   * Submit a learner's scenario action.
   * Returns consequence + reinforcements to display.
   */
  async submitScenarioAction(input: KpSubmitScenarioActionInput): Promise<KpSubmitScenarioActionResponse> {
    return kpPost<KpSubmitScenarioActionResponse>('kp-submit-scenario-action', {
      scenario_id:   input.scenario_id,
      action_id:     input.action_id,
      lesson_id:     input.lesson_id,
      slug:          input.slug,
      user_id:       input.user_id,
      time_taken_ms: input.time_taken_ms ?? 0,
    });
  },

  /**
   * Mark a lesson as complete. Call after all scenarios are played.
   * Handles XP accumulation, streak update, next lesson unlock.
   */
  async completeLesson(lessonId: string, slug: string, userId: string): Promise<KpCompleteLessonV2Response> {
    return kpPost<KpCompleteLessonV2Response>('kp-complete-lesson', {
      lesson_id: lessonId,
      slug,
      user_id:   userId,
    });
  },

  /**
   * Dismiss the narrative intro screen.
   * Safe to call multiple times (idempotent via upsert).
   */
  async dismissIntro(slug: string, userId: string): Promise<void> {
    await kpPost<{ success: boolean }>('kp-dismiss-intro', { slug, user_id: userId });
  },

  // ── AI Tutor ────────────────────────────────────────────────────────────────

  async sendTutorMessage(input: {
    world_id: string; user_id: string; slug: string;
    message: string; mode?: string; session_id?: string;
  }): Promise<{ session_id: string; reply: string; mode: string; next_suggestion?: string }> {
    return kpPost('kp-ai-tutor', input as Record<string, unknown>);
  },

  // ── Flashcards ──────────────────────────────────────────────────────────────

  async getFlashcards(worldId: string, userId: string): Promise<{ flashcards: unknown[]; due_count: number }> {
    return kpPost('kp-flashcards', { action: 'get', world_id: worldId, user_id: userId });
  },

  async generateFlashcards(worldId: string, userId: string, slug: string): Promise<{ generated_count: number }> {
    return kpPost('kp-flashcards', { action: 'generate', world_id: worldId, user_id: userId, slug });
  },

  async reviewFlashcard(input: {
    flashcard_id: string; user_id: string; world_id: string; rating: number;
  }): Promise<{ next_review_at: string; interval_days: number; new_ease_factor: number }> {
    return kpPost('kp-flashcards', { action: 'review', ...input });
  },

  // ── Study Plan ──────────────────────────────────────────────────────────────

  async getStudyPlan(worldId: string, userId: string): Promise<{
    plan: unknown; tasks_today: unknown[]; progress: unknown;
  }> {
    return kpPost('kp-study-plan', { action: 'get', world_id: worldId, user_id: userId });
  },

  async completeStudyTask(taskId: string, userId: string): Promise<{ ok: boolean; xp_earned: number }> {
    return kpPost('kp-study-plan', { action: 'complete_task', task_id: taskId, user_id: userId });
  },

  // ── Community ───────────────────────────────────────────────────────────────

  async getCommunityPosts(worldId: string, userId: string, filter?: string): Promise<{
    posts: unknown[]; total_count: number;
  }> {
    return kpPost('kp-community', { action: 'list', world_id: worldId, user_id: userId, filter: filter ?? 'all' });
  },

  async createCommunityPost(input: {
    world_id: string; user_id: string; post_type: string;
    title: string; body: string; display_name?: string;
  }): Promise<{ post: unknown }> {
    return kpPost('kp-community', { action: 'post', ...input });
  },
};
